import i18n from '@/utils/i18n';
import React from 'react';
import { Avatar, createAvatarProps } from '@usepepper/sprout-kit-ui';
import { createStyles } from '@/contexts/theme-context';
import { formatPhoneNumber } from '@/utils/phone-number';
import { RestaurantEmployee } from '@sales-portal/domain/accounts/screens/employee-list/types';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

interface EmployeeDetailsSectionProps {
  employee: RestaurantEmployee;
}

const EMPLOYEE_FIELD_HEIGHT = 40;

export const EmployeeDetailsSection: React.FC<EmployeeDetailsSectionProps> = ({
  employee,
}) => {
  const [styles, theme] = useStyles();

  const phoneNumber = employee.employee_contact_phone_number?.trim();
  const phoneDisplay = phoneNumber
    ? (formatPhoneNumber(phoneNumber) ?? phoneNumber)
    : '';

  return (
    <View style={styles.container}>
      <View style={styles.avatarContainer}>
        <Avatar
          {...createAvatarProps(
            employee.employee_name,
            employee.employee_id,
            2,
          )}
          size="$xl"
        />
      </View>
      <View style={styles.fields}>
        <View style={styles.field}>
          <Typography color={theme.sk.color.text.primary} variant="label-sm">
            {i18n.t('sales_portal.employee_edit_drawer.fields.name')}
          </Typography>
          <View style={styles.fieldValue}>
            <Typography
              color={theme.sk.color.text.primary}
              numberOfLines={1}
              variant="paragraph-md"
            >
              {employee.employee_name?.trim() || ''}
            </Typography>
          </View>
        </View>
        <View style={styles.field}>
          <Typography color={theme.sk.color.text.primary} variant="label-sm">
            {i18n.t('sales_portal.employee_edit_drawer.fields.mobile_number')}
          </Typography>
          <View style={styles.fieldValue}>
            <Typography
              color={theme.sk.color.text.primary}
              numberOfLines={1}
              variant="paragraph-md"
            >
              {phoneDisplay}
            </Typography>
          </View>
        </View>
        <View style={styles.field}>
          <Typography color={theme.sk.color.text.primary} variant="label-sm">
            {i18n.t('sales_portal.employee_edit_drawer.fields.login_email')}
          </Typography>
          <View style={styles.fieldValue}>
            <Typography
              color={theme.sk.color.text.primary}
              numberOfLines={1}
              variant="paragraph-md"
            >
              {employee.employee_login_email?.trim() || ''}
            </Typography>
          </View>
        </View>
        <View style={styles.field}>
          <Typography color={theme.sk.color.text.primary} variant="label-sm">
            {i18n.t(
              'sales_portal.employee_edit_drawer.fields.communication_email',
            )}
          </Typography>
          <View style={styles.fieldValue}>
            <Typography
              color={theme.sk.color.text.primary}
              numberOfLines={1}
              variant="paragraph-md"
            >
              {employee.employee_contact_email?.trim() || ''}
            </Typography>
          </View>
        </View>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  avatarContainer: {
    paddingBottom: theme.sk.spacing.spaceXlarge,
  },
  container: {
    alignItems: 'flex-start',
    width: '100%',
  },
  field: {
    gap: theme.sk.spacing.spaceXsmall,
    width: '100%',
  },
  fields: {
    gap: theme.sk.spacing.spaceSMedium,
    width: '100%',
  },
  fieldValue: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.input.default,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    height: EMPLOYEE_FIELD_HEIGHT,
    justifyContent: 'center',
    paddingHorizontal: theme.sk.spacing.spaceSMedium,
  },
}));
